import React, { useState, useEffect } from 'react';
import '../../css/freelancer.css';
import { useParams, Outlet, data } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import UserOwnProfile from './own/UserOwnProfile';
import FreelancerPublicProfile from './public/FreelancerPublicProfile';
import { fetchFreelancerProfile, freelancerImageUpload } from '../../services/freelancerService';
import { getFreelancerReviews } from '../../services/reviewsService';
import { toggleFavorite } from "../../services/favoritesService";
import { checkSubscription, subscribeAction } from "../../services/subcriptionsService";
import { createNotification } from "../../services/notificationsService";
import { useSelector } from "react-redux";
import EditFreelancerModal from '../../components/modals/EditFreelancerModal';
import { useDispatch } from 'react-redux';
import { patchProfile } from '../../store/profileSlice';
import Spinner from '../../utils/Spinner'

const FreelancerProfileEntry = () => {
    const { id } = useParams(); // Get the freelancer ID from the route
    const [freelancer, setFreelancer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { userId, accessToken, setProfileThumbnail } = useAuth();
    const [selectedImage, setSelectedImage] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [isModalOpen, setModalOpen] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [isSubscribed, setIsSubscribed] = useState(null);
    const [isFavorited, setIsFavorited] = useState(false);
    const [favoriteCount, setFavoriteCount] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");
    const [subscribersCount, setSubscribersCount] = useState(null); // Initialize to null instead of using freelancerData.subscribers_count
    const [profileLoading, setProfileLoading] = useState(true);
    const [reviewsLoading, setReviewsLoading] = useState(true);

    const { data: profile, status: profileStatus } = useSelector(
        (state) => state.profile
    );

    const dispatch = useDispatch();

    // 2. Initialize your local API data state
    const [freelancerData, setFreelancerData] = useState({
        id: "",
        profile_id: "",
        profession: null,
        services: "",
        portfolio_link: "",
        facebook: "",
        x: "",
        youtube: "",
        subscribers_count: 0,
        years_of_experience: 0,

        // 👇 Declare these initial values here too!
        first_name: "",
        last_name: "",
        username: "",
        contact_email: "",
        phone_number: "",
        city: "",
        state: "",
        bio: "",
        photo_original: null,
        role: "",
    });

    // 3. Compare the ROUTE ID to the Redux profile ID (cast both to strings to avoid type matching issues)
    const useReduxSource =
        !!accessToken &&
        String(profile?.freelancer_profile?.id) === String(id);

    // 4. Derive currentFreelancerData smoothly on every render pass
    const currentFreelancerData = {
        ...freelancerData,

        id: useReduxSource ? profile?.freelancer_profile?.id : freelancerData.id,
        profile_id: useReduxSource ? profile?.profile?.id : (freelancerData.profile_id || freelancerData.id),
        first_name: useReduxSource ? profile?.profile?.first_name : freelancerData.first_name,
        last_name: useReduxSource ? profile?.profile?.last_name : freelancerData.last_name,
        username: useReduxSource ? profile?.user?.username : freelancerData.username,

        contact_email: useReduxSource ? profile?.profile?.contact_email : freelancerData.contact_email,
        phone_number: useReduxSource ? profile?.profile?.phone_number : freelancerData.phone_number,

        city: useReduxSource ? profile?.profile?.city : freelancerData.city,
        state: useReduxSource ? profile?.profile?.state : freelancerData.state,
        bio: useReduxSource ? profile?.profile?.bio : freelancerData.bio,
        photo_original: useReduxSource ? profile?.profile?.photo_original : freelancerData.photo_original,
        role: useReduxSource ? profile?.profile?.role : freelancerData.role,
    };

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("photo_original", file);

        try {
            const response = await freelancerImageUpload(formData);

            const {
                photo_original,
            } = response.data;

            console.log(response.data)

            // 🔥 1. Update header + localStorage instantly
            if (photo_original) {
                setProfileThumbnail(photo_original);
                localStorage.setItem("avatar", photo_original);
            }

            // 🔥 2. Update Redux profile (SAME SHAPE as profile page)
            dispatch(
                patchProfile({
                    profile: {
                        ...profile.profile,
                        photo_original,
                    },
                    photo_original,
                })
            );

            // 🔥 3. Update local freelancer UI
            setFreelancerData(prev => ({
                ...prev,
                photo_original: photo_original
            }));

            alert("Image uploaded successfully");
        } catch (error) {
            console.error("Error uploading image:", error);
            alert("Error uploading image");
        }
    };

    const handleUpdate = (updatedFreelancer) => {
        setFreelancerData(updatedFreelancer);
    };

    const handleFavorite = async (freelancerId, favoriteType) => {
        const newState = !isFavorited;

        setIsFavorited(newState);
        setFavoriteCount(prevCount => newState ? prevCount + 1 : Math.max(prevCount - 1, 0));
        setPopupMessage(newState ? "Favorited" : "Unfavorited");

        // Show popup for 1.5 seconds
        setTimeout(() => {
            setPopupMessage("");
        }, 1500);
        try {
            await toggleFavorite(freelancerId, favoriteType);
        } catch (error) {
            console.error("Error toggling favorite:", error);
        }
    };

    const handleSubscribe = async (freelancerId) => {
        try {
            const response = await subscribeAction(freelancerId);

            if (!isSubscribed) {
                await createNotification(freelancerId);
            }

            setIsSubscribed(response.data.message.includes("Subscribed"));
            setSubscribersCount(response.data.subscribers_count);
        } catch (error) {
            alert(error.response?.data?.error || "An error occurred");
        }
    };


    // Fetch profile data on component mount
    useEffect(() => {
        const fetchFreelancerProfileData = async (id) => {
            if (!id) {
                console.log('Invalid ID:', id);
                setError('Invalid freelancer ID');
                setProfileLoading(false);
                return;
            }

            try {
                const response = await fetchFreelancerProfile(id);
                const data = response;

                setFreelancerData({
                    id: data.id ?? "",
                    profile_id: data.profile?.id || data.id || "",
                    profession: data.profession || null,
                    services: data.services || "",
                    portfolio_link: data.portfolio_link || "",
                    facebook: data.facebook || "",
                    instagram: data.instagram || "",
                    x: data.x || "",
                    youtube: data.youtube || "",
                    subscribers_count: data.subscribers_count ?? 0,
                    years_of_experience: data.years_of_experience ?? 0,

                    // 👇 Read from the nested profile object!
                    first_name: data.profile?.first_name || "",
                    last_name: data.profile?.last_name || "",
                    username: data.username || "", // keep root as this worked
                    contact_email: data.profile?.contact_email || "",
                    phone_number: data.profile?.phone_number || "",
                    city: data.profile?.city || "",
                    state: data.profile?.state || "",
                    bio: data.profile?.bio || "",
                    photo_original: data.profile?.photo_original || null,
                    role: data.role || "", // keep root
                });

                localStorage.setItem('freelancerPic', data.photo_original);
                localStorage.setItem('freelancerCover', data.cover_image);
                localStorage.setItem('freelancerProfileName', data.name);
                localStorage.setItem('freelancerServices', data.services || "");
                localStorage.setItem('freelancerPortfolioLink', data.portfolio_link || "");

                setSubscribersCount(data.subscribers_count); // Set subscribers count here

            } catch (error) {
                setError('Error fetching freelancer profile data');
            } finally {
                setProfileLoading(false);
            }
        };

        const fetchFavoriteStatus = async () => {
            try {
                const data = await toggleFavorite(id, "freelancer", "GET");
                setIsFavorited(data.is_favorited);
                setFavoriteCount(data.favorited_count);
            } catch (error) {
                console.error("Error fetching favorite status:", error);
            }
        };


        const fetchReviews = async () => {
            try {
                const data = await getFreelancerReviews(id);

                setReviews(data.reviews);

            } catch (err) {
                console.error(err);
                setError('Error fetching reviews');
            } finally {
                setReviewsLoading(false);
            }
        };

        // Fetch data
        fetchFavoriteStatus(id);
        fetchFreelancerProfileData(id);
        fetchReviews(id);
    }, [id]);

    // Show the loading spinner or error message while loading
    if (profileLoading || reviewsLoading) {
        return (
            <div className='profile-spinner-container'>
                <div className='centered-content'>
                    <div className="profile-spinner-wrapper">
                        <div className="profile-spinner"></div>
                    </div>
                </div>
            </div>
        );
    }
    if (error) return <div>{error}</div>;


    const isOwnProfile =
        !!accessToken &&
        profile?.freelancer_profile?.id === freelancerData.id;

    return isOwnProfile ? (
        <>
            <UserOwnProfile
                freelancerData={currentFreelancerData}
                setFreelancerData={setFreelancerData}
                handleUpload={handleUpload}
                setModalOpen={setModalOpen}
                isModalOpen={isModalOpen}
                handleUpdate={handleUpdate}
                reviews={reviews}
                setProfileThumbnail={setProfileThumbnail}
            />

            {/* Modal */}
            {isModalOpen && (
                <EditFreelancerModal
                    freelancerData={currentFreelancerData}
                    onClose={() => setModalOpen(false)}
                    handleUpdate={handleUpdate}
                    setProfileThumbnail={setProfileThumbnail}
                />
            )}
        </>
    ) : (
        <FreelancerPublicProfile
            defaultTab="Services"
            outletContext={{
                freelancerData: currentFreelancerData,
                popupMessage,
                isSubscribed,
                isFavorited,
                favoriteCount,
                subscribersCount,
                reviews,
                handleFavorite,
                handleSubscribe,
            }}
        />
    )
};

export default FreelancerProfileEntry;
